'use client';

import { Card, CardContent, CardHeader, CardTitle } from '@govinity/ui/components/ui/card';
import { MeetingStatus, resolveMeetingConfig } from '@govinity/validation';
import type { MeetingEventPublicGetResponse } from '@govinity/validation/public';
import { Mic } from 'lucide-react';
import { useTranslations } from 'next-intl';

import { useAuthStore } from '@/stores/authStore';
import { SignupList } from './signup-list';
import { useUserSignups } from './use-user-signups';
import { useGovernmentContext } from '../../../context';

type SignupStatusCardProps = {
  meeting: Pick<MeetingEventPublicGetResponse, 'id' | 'status'>;
};

/**
 * Standing reminder on the meeting page that the citizen is on the speaker list, so the commitment
 * is visible without opening the sign-up dialog. Renders nothing unless they have a live sign-up.
 */
export function SignupStatusCard({ meeting }: SignupStatusCardProps) {
  const { government } = useGovernmentContext();
  const user = useAuthStore((state) => state.user);
  const t = useTranslations('PortalPublicSpeakerSignup');

  const { publicSpeakers } = resolveMeetingConfig(government.meetingConfig);
  const isSignupPossible =
    publicSpeakers.enabled && meeting.status === MeetingStatus.Upcoming && !!user;

  const { data: signups } = useUserSignups(government.code, meeting.id, isSignupPossible);

  if (!signups?.records.length) {
    return null;
  }

  return (
    <Card className="bg-info-alt2-light mb-4 border-l-4">
      <CardHeader>
        <CardTitle className="text-info-alt2-foreground flex items-center gap-2 text-base">
          <Mic aria-hidden="true" className="size-4" />
          {t('statusCardTitle')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <SignupList records={signups.records} />
      </CardContent>
    </Card>
  );
}
